import { useMemo, useState } from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { SplashScreen } from 'src/components/loading-screen';

import { DashboardV3RangeSelector } from '../../components/DashboardV3RangeSelector';
import { useDashboardV3Range } from '../../hooks/useDashboardV3Range';
import { buildMonotoneSplinePath } from '../../utils/monotoneSpline';
import {
    useDashboardV3OverviewData,
    type DashboardV3OverviewTabValue,
} from '../../hooks/useDashboardV3OverviewData';
import {
    OVERVIEW_PRODUCT_TABS,
    OVERVIEW_RANGE_LABELS,
    type OverviewRangeValue,
} from '../../mock-data';
import type {
    DashboardV3OverviewDepositToTradeConversion,
    DashboardV3OverviewFunnelData,
    DashboardV3OverviewFunnelProduct,
    DashboardV3OverviewKpiData,
    DashboardV3OverviewProductKpiData,
    DashboardV3OverviewProductType,
    DashboardV3OverviewRetentionByProduct,
    DashboardV3OverviewRetentionCell,
    DashboardV3OverviewRetentionMatrix,
    DashboardV3OverviewTrendData,
} from '../../services/dashboardV3.types';

const ACCENT_COLORS: Record<string, string> = {
    primary: '#BB86FC',
    secondary: '#03DAC6',
    success: '#81C995',
    warning: '#FFB74D',
    info: '#64B5F6',
    error: '#CF6679',
};

// 跟入金模块 PRODUCT_COLORS 保持一致的产品配色（DepositsPage.tsx），总览 tab 也用同一套认知
const OVERVIEW_TAB_COLORS: Record<string, string> = {
    overview: '#BB86FC',
    perps: '#FFB74D',
    predictSpot: '#03DAC6',
    predictLeveraged: '#BB86FC',
    predictGrid: '#81C995',
};

const OVERVIEW_PRODUCT_ACCENT: Record<DashboardV3OverviewProductType, string> = {
    perps: '#FFB74D',
    prediction_spot: '#03DAC6',
    prediction_leverage: '#BB86FC',
    prediction_grid: '#81C995',
};

type ProductTabValue = DashboardV3OverviewTabValue;
type TranslateFn = ReturnType<typeof useTranslate>;

type MetricCardItem = {
    key: string;
    title: string;
    value: string;
    delta: string | null;
    deltaNote: string;
    subtitle: string;
    variant: keyof typeof ACCENT_COLORS;
    trend: 'up' | 'down';
    // 有值时优先于 variant，用于产品子 tab 卡片跟着 tab 颜色走（顶部这条线）
    color?: string;
};

type FunnelItem = {
    key: string;
    label: string;
    value: number | null;
    color: string;
    rateText: string;
};

type TrendItem = {
    key: string;
    title: string;
    volumeLabel: string;
    barColor: string;
    data: Array<{ date: string; volume: number; users: number }>;
};

type RetentionCell = {
    value: string;
    // 该格自己的样本量（对应周期的新交易用户数），D+1/周/月三个队列各不相同
    sample: string;
    delta?: string | null;
    trend?: 'up' | 'down';
};

type RetentionRow = {
    label: string;
    isCurrent: boolean;
    d1: RetentionCell;
    weekly: RetentionCell;
    monthly: RetentionCell;
};

const OVERVIEW_PRODUCT_LABELS: Record<DashboardV3OverviewProductType, { key: string; fallback: string }> = {
    perps: { key: 'module.dashboardV3.overview.products.perps', fallback: 'Perpetuals' },
    prediction_spot: { key: 'module.dashboardV3.overview.products.predictionSpot', fallback: 'Prediction Spot' },
    prediction_leverage: { key: 'module.dashboardV3.overview.products.predictionLeverage', fallback: 'Prediction Leverage' },
    prediction_grid: { key: 'module.dashboardV3.overview.products.predictionGrid', fallback: 'Prediction Grid' },
};

const PRODUCT_META: Record<DashboardV3OverviewProductType, { key: string; titleKey: string; fallbackTitle: string; barColor: string }> = {
    perps: {
        key: 'perps',
        titleKey: 'module.dashboardV3.overview.trend.perpsTitle',
        fallbackTitle: 'Perpetuals Trading Trend',
        barColor: '#C9963D',
    },
    prediction_spot: {
        key: 'predictSpot',
        titleKey: 'module.dashboardV3.overview.trend.predictionSpotTitle',
        fallbackTitle: 'Prediction Spot Trading Trend',
        barColor: '#26A99A',
    },
    prediction_leverage: {
        key: 'predictLeveraged',
        titleKey: 'module.dashboardV3.overview.trend.predictionLeverageTitle',
        fallbackTitle: 'Prediction Leverage Trading Trend',
        barColor: '#9575CD',
    },
    prediction_grid: {
        key: 'predictGrid',
        titleKey: 'module.dashboardV3.overview.trend.predictionGridTitle',
        fallbackTitle: 'Prediction Grid Trading Trend',
        barColor: '#6EA66F',
    },
};

const TREND_VOLUME_LABELS: Record<DashboardV3OverviewProductType, { key: string; fallback: string }> = {
    perps: { key: 'module.dashboardV3.overview.trend.perpsVolume', fallback: 'Perpetuals Volume' },
    prediction_spot: { key: 'module.dashboardV3.overview.trend.predictionSpotVolume', fallback: 'Prediction Spot Volume' },
    prediction_leverage: { key: 'module.dashboardV3.overview.trend.predictionLeverageVolume', fallback: 'Prediction Leverage Volume' },
    prediction_grid: { key: 'module.dashboardV3.overview.trend.predictionGridVolume', fallback: 'Prediction Grid Volume' },
};

function productLabel(t: TranslateFn, productType: DashboardV3OverviewProductType) {
    const item = OVERVIEW_PRODUCT_LABELS[productType] ?? OVERVIEW_PRODUCT_LABELS.perps;
    return t(item.key, { _: item.fallback });
}

function formatInteger(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    return Math.round(Number(value)).toLocaleString();
}

/**
 * 与入金 tab 的 formatMoney 完全一致（min 0 / max 2 位小数）：同一个后端字段在两个页面上
 * 必须显示成同一个数。之前这里用 Math.round，$496.25 被砍成 $496，被当成两边数据对不上。
 * min 0 是为了整数金额不拖一条没意义的 .00。
 */
const MONEY_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

function formatMoney(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    return MONEY_FORMATTER.format(Number(value));
}

function formatPercent(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    return `${Number(value).toFixed(1)}%`;
}

function formatDelta(value: number | null | undefined) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const n = Number(value);
    return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function trendOf(value: number | null | undefined): 'up' | 'down' {
    return Number(value ?? 0) < 0 ? 'down' : 'up';
}

function formatDateLabel(value: string) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return value.slice(5, 10);
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}

function buildOverviewKpiCards(kpi: DashboardV3OverviewKpiData, t: TranslateFn): MetricCardItem[] {
    return [
        {
            key: 'registeredUsers',
            title: t('module.dashboardV3.overview.kpi.newUsers', { _: 'New Registered Users' }),
            value: formatInteger(kpi.newUsers),
            delta: formatDelta(kpi.newUsersDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.newUsersSubtitle', { _: 'Unique accounts created in the selected period' }),
            variant: 'success',
            trend: trendOf(kpi.newUsersDelta),
        },
        {
            key: 'depositUsers',
            title: t('module.dashboardV3.overview.kpi.depositUsers', { _: 'Deposit Users' }),
            value: formatInteger(kpi.depositUsers),
            delta: formatDelta(kpi.depositUsersDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.depositUsersSubtitle', { _: 'Unique users with deposit behavior in the selected period' }),
            variant: 'primary',
            trend: trendOf(kpi.depositUsersDelta),
        },
        {
            key: 'tradingUsers',
            title: t('module.dashboardV3.overview.kpi.tradingUsers', { _: 'Trading Users' }),
            value: formatInteger(kpi.tradingUsers),
            delta: formatDelta(kpi.tradingUsersDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.tradingUsersSubtitle', { _: 'Unique users with trading behavior in the selected period' }),
            variant: 'secondary',
            trend: trendOf(kpi.tradingUsersDelta),
        },
    ];
}

/** 每个产品固定一个强调色，跟趋势图的 barColor 同一色系，四个产品的卡片视觉上能对上号。 */
const PRODUCT_ACCENT: Record<DashboardV3OverviewProductType, keyof typeof ACCENT_COLORS> = {
    perps: 'warning',
    prediction_spot: 'secondary',
    prediction_leverage: 'primary',
    prediction_grid: 'success',
};

/**
 * 2026-09-08 拆分：入金→交易转化率不再是跨产品的单一数字——分子「既入金又交易」用户数
 * 没法跨产品去重，改成四个产品各一张卡，分子是上游给的 depositTradingUsers（真子集），
 * 不会再出现 >100% 的情况。
 */
function buildDepositToTradeCards(
    conversions: DashboardV3OverviewDepositToTradeConversion[],
    t: TranslateFn
): MetricCardItem[] {
    return conversions.map((item) => {
        const product = productLabel(t, item.productType);

        return {
            key: `depositToTrade-${item.productType}`,
            title: t('module.dashboardV3.overview.kpi.depositToTradeRateByProduct', {
                product,
                _: `${product} Deposit → Trade`,
            }),
            value: formatPercent(item.rate),
            delta: formatDelta(item.rateDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: `${formatInteger(item.depositTradingUsers)} / ${formatInteger(item.depositUsers)}`,
            variant: PRODUCT_ACCENT[item.productType],
            trend: trendOf(item.rateDelta),
        };
    });
}

/**
 * 2026-09-08 拆分：D+1 留存率也改成四个产品各一张卡，直接透传留存模块自己的 daily 卡片
 * ——保证总览这几张卡、留存模块页面、转化漏斗最后一级三处永远是同一个数。
 * 队列固定「昨天新交易用户 → 今天」，不受右上角时间筛选影响。
 */
function buildRetentionByProductCards(
    items: DashboardV3OverviewRetentionByProduct[],
    t: TranslateFn
): MetricCardItem[] {
    return items.map((item) => {
        const product = productLabel(t, item.productType);
        const hasCohort = Number.isFinite(item.cohortUsers) && item.cohortUsers > 0;

        return {
            key: `d1Retention-${item.productType}`,
            title: t('module.dashboardV3.overview.kpi.d1RetentionRateByProduct', {
                product,
                _: `${product} D+1 Retention`,
            }),
            value: formatPercent(hasCohort ? item.retentionRate : null),
            delta: hasCohort ? formatDelta(item.retentionRateDelta) : null,
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: `${formatInteger(item.retainedUsers)} / ${formatInteger(item.cohortUsers)}`,
            variant: PRODUCT_ACCENT[item.productType],
            trend: trendOf(item.retentionRateDelta),
        };
    });
}

function buildFinanceCards(kpi: DashboardV3OverviewKpiData, t: TranslateFn): MetricCardItem[] {
    return [
        {
            key: 'platformTvl',
            title: t('module.dashboardV3.overview.kpi.platformTvl', { _: 'Platform TVL' }),
            /**
             * 不要 `|| 0` 兜底：后端拿不到余额时返回 null，兜成 0 会把「没数据」显示成 $0；
             * 环比更是如此——历史快照还没有，后端恒定返回 null，兜成 0 就会一直显示「↑ +0.0% vs 7日前」，
             * 看着像真算过一样。null 交给卡片渲染成「—」。
             */
            value: formatMoney(kpi.platformTvl),
            delta: formatDelta(kpi.platformTvlDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsSevenDaysAgo', { _: 'vs 7 days ago' }),
            subtitle: t('module.dashboardV3.overview.kpi.platformTvlSubtitle', { _: 'Real-time snapshot · Not affected by time filters' }),
            variant: 'primary',
            trend: trendOf(kpi.platformTvlDelta),
        },
        {
            key: 'depositVolume',
            title: t('module.dashboardV3.overview.kpi.depositAmount', { _: 'Deposit Amount' }),
            value: formatMoney(kpi.depositAmount),
            delta: formatDelta(kpi.depositAmountDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.depositAmountSubtitle', { _: 'Total on-chain deposits into the platform' }),
            variant: 'success',
            trend: trendOf(kpi.depositAmountDelta),
        },
        {
            key: 'withdrawVolume',
            title: t('module.dashboardV3.overview.kpi.withdrawAmount', { _: 'Withdrawal Amount' }),
            value: formatMoney(kpi.withdrawAmount),
            delta: formatDelta(kpi.withdrawAmountDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.withdrawAmountSubtitle', { _: 'Total on-chain withdrawals from the platform' }),
            variant: 'error',
            trend: trendOf(kpi.withdrawAmountDelta),
        },
    ];
}

function buildProductKpiCards(kpi: DashboardV3OverviewProductKpiData, t: TranslateFn): MetricCardItem[] {
    const color = OVERVIEW_PRODUCT_ACCENT[kpi.productType];

    return [
        {
            key: 'tradingUsers',
            title: t('module.dashboardV3.overview.kpi.tradingUsers', { _: 'Trading Users' }),
            value: formatInteger(kpi.tradingUsers),
            delta: formatDelta(kpi.tradingUsersDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.tradingUsersSubtitle', { _: 'Unique users with trading behavior in the selected period' }),
            variant: 'warning',
            trend: trendOf(kpi.tradingUsersDelta),
            color,
        },
        {
            key: 'tradingVolume',
            title: t('module.dashboardV3.overview.kpi.tradingVolume', { _: 'Trading Volume' }),
            value: formatMoney(kpi.tradingVolume),
            delta: formatDelta(kpi.tradingVolumeDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.tradingVolumeSubtitle', { _: 'Total trading volume in the period' }),
            variant: 'warning',
            trend: trendOf(kpi.tradingVolumeDelta),
            color,
        },
        {
            key: 'depositAmount',
            title: t('module.dashboardV3.overview.kpi.productDepositAmount', { _: 'Deposit Amount' }),
            value: formatMoney(kpi.depositAmount),
            delta: formatDelta(kpi.depositAmountDelta),
            deltaNote: t('module.dashboardV3.overview.common.vsPrevious', { _: 'vs Previous' }),
            subtitle: t('module.dashboardV3.overview.kpi.depositAmountSubtitle', { _: 'Total on-chain deposits into the platform' }),
            variant: 'warning',
            trend: trendOf(kpi.depositAmountDelta),
            color,
        },
        // 「首入用户」卡片暂时下线：wallet 的 GetDepositKpi 没有首次入金维度，
        // 等上游补上 first_deposit_users 字段后再恢复
    ];
}

/**
 * 2026-09-08 拆分：每个产品一条独立漏斗。「注册」是平台共用的新注册数（newUsers 单独传，
 * 不在 DashboardV3OverviewFunnelProduct 里），从「入金」开始才是这个产品自己的数字。
 *
 * 「交易」这一级现在是 depositTradingUsers（该产品「入金且交易」的子集），保证不会比
 * 上一级「入金」的人数还多；「D+1 留存」是留存模块自己的 daily 卡片直接透传，队列是
 * 「昨天新交易用户」，跟前面几级不是同一批人，只是并列展示在同一个漏斗里。
 */
function buildFunnelItems(
    newUsers: number,
    funnel: DashboardV3OverviewFunnelProduct,
    t: TranslateFn
): FunnelItem[] {
    return [
        {
            key: 'registered',
            label: t('module.dashboardV3.overview.funnel.newUsers', { _: 'New Registered Users' }),
            value: newUsers,
            color: '#81C995',
            rateText: '',
        },
        {
            key: 'deposit',
            label: t('module.dashboardV3.overview.funnel.depositUsers', { _: 'Deposit Users' }),
            value: funnel.depositUsers,
            color: '#BB86FC',
            rateText: `↓ ${t('module.dashboardV3.overview.funnel.registerToDeposit', { _: 'Register→Deposit' })} ${formatPercent(funnel.registerToDepositRate)}`,
        },
        {
            key: 'trading',
            label: t('module.dashboardV3.overview.funnel.tradingUsers', { _: 'Trading Users' }),
            value: funnel.tradingUsers,
            color: '#03DAC6',
            rateText: `↓ ${t('module.dashboardV3.overview.funnel.depositToTrade', { _: 'Deposit→Trade' })} ${formatPercent(funnel.depositToTradeRate)}`,
        },
        {
            key: 'retention',
            label: t('module.dashboardV3.overview.funnel.retainedUsers', { _: 'Next-day Retained' }),
            value: funnel.retainedUsers,
            color: '#64B5F6',
            rateText: `↓ ${t('module.dashboardV3.overview.funnel.d1Retention', { _: 'D+1 Retention' })} ${formatPercent(funnel.d1RetentionRate)}`,
        },
    ];
}

function buildTrendItems(trends: DashboardV3OverviewTrendData[], t: TranslateFn): TrendItem[] {
    return trends.map((trend) => {
        const meta = PRODUCT_META[trend.productType] ?? PRODUCT_META.perps;
        const volumeLabel = TREND_VOLUME_LABELS[trend.productType] ?? TREND_VOLUME_LABELS.perps;
        return {
            key: meta.key,
            title: t(meta.titleKey, { _: meta.fallbackTitle }),
            barColor: meta.barColor,
            volumeLabel: t(volumeLabel.key, { _: volumeLabel.fallback }),
            data: (trend.rows ?? []).map((row) => ({
                date: formatDateLabel(row.date),
                volume: Number(row.tradingVolume || 0),
                users: Number(row.tradingUsers || 0),
            })),
        };
    });
}

function OverviewPanel({
    title,
    right,
    children,
    sx,
}: {
    title: string;
    right?: React.ReactNode;
    children: React.ReactNode;
    sx?: any;
}) {
    return (
        <Paper
            sx={{
                bgcolor: '#232323',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                borderRadius: 0.8,
                overflow: 'hidden',
                ...sx,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    px: 2.75,
                    py: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
            >
                <Typography sx={{ color: '#FFFFFF', fontSize: 20, fontWeight: 500 }}>
                    {title}
                </Typography>
                {right}
            </Stack>

            <Box sx={{ p: 2.75 }}>{children}</Box>
        </Paper>
    );
}

/**
 * 2026-09-08：留存矩阵、趋势图这两块渲染逻辑比卡片复杂（自己的 SVG/表格），不值得为了
 * loading 状态改造成 Skeleton，沿用原来"整页 SplashScreen 蒙层"的视觉效果，只是把范围
 * 缩小到各自的区块自己的容器里——只在"这块还没有任何数据"时盖住，已经有数据后台静默
 * 刷新不会再蒙一层挡住已有内容。
 */
function SectionLoadingOverlay({ show }: { show: boolean }) {
    if (!show) return null;

    return (
        <SplashScreen
            portal={false}
            sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 8,
                height: '100%',
                bgcolor: 'rgba(13,13,13,0.68)',
                backdropFilter: 'blur(1px)',
            }}
        />
    );
}

/**
 * 2026-09-08：加了 loading，参考 Referral 模块 KpiCard 的做法——卡片外壳（标题、
 * 强调色条）先出来，数值/环比/副标题用 Skeleton 占位，等这一块自己的数据回来了再替换成
 * 真实内容。不再是"整个页面转圈，全部数据都到齐才一次性显示"。
 */
function OverviewMetricCard({ item, loading = false }: { item: MetricCardItem; loading?: boolean }) {
    const deltaColor = item.trend === 'down' ? '#CF6679' : '#81C995';

    return (
        <Paper
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#232323',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 0.6,
                minHeight: 132,
                p: 2.2,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: '0 0 auto 0',
                    height: 3,
                    bgcolor: item.color ?? ACCENT_COLORS[item.variant],
                }}
            />

            <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13, mb: 1.2 }}>
                {item.title}
            </Typography>

            <Typography
                sx={{
                    color: '#FFFFFF',
                    fontSize: 31,
                    lineHeight: 1.1,
                    fontWeight: 300,
                    letterSpacing: 0.5,
                }}
            >
                {loading ? <Skeleton width={100} height={38} sx={{ bgcolor: 'rgba(255,255,255,0.11)' }} /> : item.value}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1.2 }}>
                {loading ? (
                    <Skeleton width={90} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ) : (
                    <>
                        <Typography sx={{ color: deltaColor, fontSize: 13 }}>
                            {item.delta ? `${item.trend === 'down' ? '↓' : '↑'} ${item.delta.replace('-', '')}` : '—'}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                            {item.deltaNote}
                        </Typography>
                    </>
                )}
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 12, mt: 0.8 }}>
                {loading ? <Skeleton width="70%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} /> : item.subtitle}
            </Typography>
        </Paper>
    );
}

function FinanceMetricCard({ item, loading = false }: { item: MetricCardItem; loading?: boolean }) {
    return (
        <OverviewMetricCard item={item} loading={loading} />
    );
}

/**
 * 各平台用户体系没打通、无法跨产品去重，所以留存矩阵按四个交易类型分开各出一张。
 *
 * 样本量写在每个格子里、不再单出一列：D+1 / 周 / 月是三个不同的队列，样本量本来就不相等，
 * 单列一列说不清它属于哪个指标（Sami 提、Tc 确认）。
 */
function RetentionMatrix({ matrix, t }: { matrix: DashboardV3OverviewRetentionMatrix; t: TranslateFn }) {
    const sampleLabel = t('module.dashboardV3.overview.retention.sample', { _: 'Sample' });

    const cell = (item: DashboardV3OverviewRetentionCell, withDelta: boolean): RetentionCell => {
        /**
         * 队列人数为 0 时留存率没有分母，上游按 0% 返回，这里统一显示「—」，
         * 免得和「有新用户但一个都没回来」混成同一个 0.0%——与留存 tab 的 KPI 卡同一套判断，
         * 否则同一个指标两个页面会显示不同的值。环比同理失去意义。
         */
        const hasCohort = Number.isFinite(item.cohortUsers) && item.cohortUsers > 0;

        return {
            value: formatPercent(hasCohort ? item.retentionRate : null),
            sample: formatInteger(item.cohortUsers),
            ...(withDelta
                ? {
                    delta: hasCohort ? formatDelta(item.retentionRateDelta) : null,
                    trend: trendOf(item.retentionRateDelta),
                }
                : {}),
        };
    };

    const rows: RetentionRow[] = [
        {
            label: t('module.dashboardV3.overview.common.current', { _: 'Current' }),
            isCurrent: true,
            d1: cell(matrix.current.d1, true),
            weekly: cell(matrix.current.weekly, true),
            monthly: cell(matrix.current.monthly, true),
        },
        {
            // 上期只展示数值，环比Δ 只标在本期行（PRD 4.3）
            label: t('module.dashboardV3.overview.common.previous', { _: 'Previous' }),
            isCurrent: false,
            d1: cell(matrix.prior.d1, false),
            weekly: cell(matrix.prior.weekly, false),
            monthly: cell(matrix.prior.monthly, false),
        },
    ];
    const product = productLabel(t, matrix.productType);

    return (
        <OverviewPanel
            title={t('module.dashboardV3.overview.retention.matrixTitle', {
                product,
                _: `${product} Retention Matrix`,
            })}
            sx={{ height: '100%' }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '70px repeat(3, minmax(130px, 1fr))',
                    gap: 1,
                    alignItems: 'center',
                    color: '#FFFFFF',
                }}
            >
                <Box />
                {[
                    t('module.dashboardV3.overview.retention.d1', { _: 'D+1' }),
                    t('module.dashboardV3.overview.retention.weekly', { _: 'Weekly' }),
                    t('module.dashboardV3.overview.retention.monthly', { _: 'Monthly' }),
                ].map((item) => (
                    <Typography
                        key={item}
                        sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13, textAlign: 'center' }}
                    >
                        {item}
                    </Typography>
                ))}

                {rows.map((row) => (
                    <Box key={row.label} sx={{ display: 'contents' }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: 14 }}>
                            {row.label}
                        </Typography>

                        {[row.d1, row.weekly, row.monthly].map((cell, index) => {
                            const bg =
                                index === 0
                                    ? 'rgba(187,134,252,0.16)'
                                    : index === 1
                                        ? 'rgba(3,218,198,0.13)'
                                        : 'rgba(100,181,246,0.13)';

                            const color = index === 0 ? '#BB86FC' : index === 1 ? '#03DAC6' : '#64B5F6';

                            return (
                                <Box
                                    key={`${row.label}-${index}`}
                                    sx={{
                                        minHeight: 62,
                                        px: 1,
                                        py: 0.8,
                                        borderRadius: 0.5,
                                        bgcolor: row.isCurrent ? bg : 'rgba(255,255,255,0.04)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Stack direction="row" alignItems="baseline" spacing={0.7}>
                                        <Typography sx={{ color: row.isCurrent ? color : 'rgba(255,255,255,0.55)', fontSize: 22 }}>
                                            {cell.value}
                                        </Typography>

                                        {/* 样本量跟着各自的指标走，不再单出一列 */}
                                        <Typography
                                            sx={{
                                                color: 'rgba(255,255,255,0.58)',
                                                fontSize: 11,
                                                px: 0.6,
                                                py: 0.15,
                                                borderRadius: 0.4,
                                                bgcolor: 'rgba(255,255,255,0.08)',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {`${sampleLabel}: ${cell.sample}`}
                                        </Typography>
                                    </Stack>

                                    {'delta' in cell && (
                                        <Typography
                                            sx={{
                                                color: !cell.delta
                                                    ? 'rgba(255,255,255,0.45)'
                                                    : cell.trend === 'down'
                                                        ? '#CF6679'
                                                        : '#81C995',
                                                fontSize: 12,
                                                mt: 0.3,
                                            }}
                                        >
                                            {cell.delta
                                                ? `${cell.trend === 'down' ? '↓' : '↑'} ${cell.delta.replace('-', '')}`
                                                : '—'}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </OverviewPanel>
    );
}

function ConversionFunnel({
    items,
    t,
    productType,
}: {
    items: FunnelItem[];
    t: TranslateFn;
    // 2026-09-08 拆分：不传就是旧的单一标题（暂无调用方这么用了，留着兼容）；
    // 传了就渲染成"{产品} Conversion Funnel"，跟留存矩阵、趋势图的每卡一个产品标题一致。
    productType?: DashboardV3OverviewProductType;
}) {
    /**
     * 漏斗的基线是第一级（新注册用户），后面每一级都相对它取宽度。
     * 之前取的是所有级里的最大值，导致 1/1/1/2 这种数据下第一级只画到 50%——
     * 第一级永远应该是满格。
     */
    const baseline = Math.max(1, Number(items[0]?.value || 0));
    const title = productType
        ? t('module.dashboardV3.overview.funnel.titleByProduct', {
            product: productLabel(t, productType),
            _: `${productLabel(t, productType)} Conversion Funnel`,
        })
        : t('module.dashboardV3.overview.funnel.title', { _: 'Conversion Funnel' });

    return (
        <OverviewPanel
            title={title}
            right={
                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                    {t('module.dashboardV3.overview.funnel.path', { _: 'Register → Deposit → Trade → Retain' })}
                </Typography>
            }
            sx={{ height: '100%' }}
        >
            <Stack spacing={2.2}>
                {items.map((item) => {
                    const numericValue = Number(item.value || 0);
                    /**
                     * 次日留存的队列是「昨天的新交易用户」，跟前三级不是同一批人，
                     * 人数可能反超新注册用户，所以要截到 100%，避免进度条画出容器。
                     */
                    const ratio = Math.min(1, numericValue / baseline);
                    const percent = numericValue > 0 ? `${Math.max(4, ratio * 100)}%` : '0%';

                    return (
                        <Box key={item.key}>
                            {/* 转化率画在它连接的两级中间：每一级带的 rateText 描述的是「上一级 → 这一级」 */}
                            {item.rateText && (
                                <Typography
                                    sx={{
                                        mb: 2.2,
                                        color: 'rgba(255,255,255,0.45)',
                                        fontSize: 12,
                                        textAlign: 'center',
                                    }}
                                >
                                    {item.rateText}
                                </Typography>
                            )}

                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 14 }}>
                                    {item.label}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 14 }}>
                                    {item.value === null ? '—' : item.value.toLocaleString()}
                                </Typography>
                            </Stack>

                            <Box
                                sx={{
                                    height: 8,
                                    borderRadius: 10,
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: percent,
                                        height: '100%',
                                        borderRadius: 10,
                                        bgcolor: item.color,
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </OverviewPanel>
    );
}

function moneyCompact(value: number) {
    const amount = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (amount >= 1000000) return `${sign}$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `${sign}$${(amount / 1000).toFixed(2)}K`;
    return `${sign}$${amount.toFixed(2)}`;
}

function tooltipMoney(value: number) {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${Math.round(value / 1000).toLocaleString()}K`;
    return `$${Math.round(value).toLocaleString()}`;
}

// SVG <text> doesn't wrap on its own, so the tooltip box has to be sized to fit its
// longest line up front. There's no cheap DOM text-measurement available inline here,
// so this is a rough-but-safe average-character-width estimate (biased wide on purpose
// to avoid clipping real proportional-font text like "Prediction Leverage Volume: $16").
function estimateTextWidth(text: string, fontSize: number) {
    return text.length * fontSize * 0.62;
}

function ComboTrendChart({
    data,
    barColor,
    volumeLabel,
    usersLabel,
    userUnit,
    fullWidth = false,
}: {
    data: readonly { date: string; volume: number; users: number }[];
    barColor: string;
    volumeLabel: string;
    usersLabel: string;
    userUnit: string;
    fullWidth?: boolean;
}) {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const width = fullWidth ? 1120 : 720;
    const height = fullWidth ? 340 : 300;
    const left = fullWidth ? 92 : 60;
    const right = fullWidth ? 82 : 55;
    const top = fullWidth ? 30 : 26;
    const bottom = fullWidth ? 48 : 42;
    const edgePadding = fullWidth ? 48 : 28;
    const plotLeft = left + edgePadding;
    const plotRight = width - right - edgePadding;
    const chartWidth = plotRight - plotLeft;
    const chartHeight = height - top - bottom;

    const maxVolume = Math.max(1, ...data.map((item) => item.volume)) * 1.15;
    const maxUsers = Math.max(1, ...data.map((item) => item.users)) * 1.15;
    const stepX = chartWidth / Math.max(1, data.length - 1);
    const barWidth = Math.min(fullWidth ? 40 : 34, Math.max(12, stepX * 0.52));
    const xForIndex = (index: number) =>
        data.length <= 1 ? (plotLeft + plotRight) / 2 : plotLeft + index * stepX;

    const points = data.map((item, index) => {
        const x = xForIndex(index);
        const y = top + chartHeight - (item.users / maxUsers) * chartHeight;
        return { x, y };
    });

    const linePath = buildMonotoneSplinePath(points);
    const activeIndex = hoveredIndex !== null && data[hoveredIndex] ? hoveredIndex : null;
    const activeItem = activeIndex === null ? null : data[activeIndex];
    const activePoint = activeIndex === null ? null : points[activeIndex];
    const tooltipVolumeText = activeItem ? `${volumeLabel}: ${tooltipMoney(activeItem.volume)}` : '';
    const tooltipUsersText = activeItem
        ? `${usersLabel}: ${formatInteger(activeItem.users)} ${userUnit}`
        : '';
    const tooltipPaddingX = 14;
    // Width has to fit the longest of the three lines (date / volume / users) plus padding
    // on both sides — a fixed 170px box overflows as soon as a product label is long
    // (e.g. "Prediction Leverage Volume: $16" ran past the box edge).
    const tooltipContentWidth = Math.max(
        estimateTextWidth(activeItem?.date ?? '', 13),
        estimateTextWidth(tooltipVolumeText, 13),
        estimateTextWidth(tooltipUsersText, 13),
    );
    const tooltipWidth = Math.max(170, Math.ceil(tooltipContentWidth) + tooltipPaddingX * 2);
    const tooltipHeight = 82;
    const tooltipX = activePoint
        ? Math.max(left + 6, Math.min(activePoint.x + 14, width - right - tooltipWidth - 6))
        : 0;
    const tooltipY = activePoint
        ? Math.max(top + 6, Math.min(activePoint.y + 22, height - bottom - tooltipHeight - 6))
        : 0;

    return (
        <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
                width: '100%',
                height: fullWidth
                    ? { xs: 300, md: 'clamp(340px, 23vw, 430px)' }
                    : { xs: 280, md: 330 },
                display: 'block',
            }}
        >
            {[0, 0.5, 1].map((ratio) => {
                const y = top + chartHeight - ratio * chartHeight;
                return (
                    <g key={ratio}>
                        <line
                            x1={left}
                            x2={width - right}
                            y1={y}
                            y2={y}
                            stroke="rgba(255,255,255,0.12)"
                            strokeDasharray="4 4"
                        />
                        <text
                            x={left - 12}
                            y={y + 4}
                            textAnchor="end"
                            fill="rgba(255,255,255,0.42)"
                            fontSize="12"
                        >
                            {moneyCompact(maxVolume * ratio)}
                        </text>
                        <text
                            x={width - right + 12}
                            y={y + 4}
                            textAnchor="start"
                            fill="rgba(255,255,255,0.42)"
                            fontSize="12"
                        >
                            {Math.round(maxUsers * ratio)}
                        </text>
                    </g>
                );
            })}

            {data.map((item, index) => {
                const x = xForIndex(index);
                const barHeight = (item.volume / maxVolume) * chartHeight;
                const y = top + chartHeight - barHeight;

                return (
                    <g key={item.date}>
                        <rect
                            x={x - barWidth / 2}
                            y={y}
                            width={barWidth}
                            height={barHeight}
                            rx="3"
                            fill={barColor}
                            opacity="0.9"
                        />
                        <text
                            x={x}
                            y={height - 12}
                            textAnchor="middle"
                            fill="rgba(255,255,255,0.38)"
                            fontSize="12"
                        >
                            {item.date}
                        </text>
                    </g>
                );
            })}

            <path d={linePath} fill="none" stroke="#BB86FC" strokeWidth="3" strokeLinecap="round" />

            {points.map((point, index) => (
                <circle
                    key={index}
                    cx={point.x}
                    cy={point.y}
                    r="2.5"
                    fill="#BB86FC"
                    opacity="0.95"
                />
            ))}

            {data.map((item, index) => {
                const x = xForIndex(index);

                return (
                    <rect
                        key={`hit-${item.date}`}
                        x={x - Math.max(stepX / 2, 16)}
                        y={top}
                        width={Math.max(stepX, 32)}
                        height={chartHeight}
                        fill="transparent"
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseMove={() => setHoveredIndex(index)}
                        style={{ cursor: 'crosshair' }}
                    />
                );
            })}

            {activeItem && activePoint && (
                <g pointerEvents="none">
                    <line
                        x1={activePoint.x}
                        x2={activePoint.x}
                        y1={top}
                        y2={top + chartHeight}
                        stroke="rgba(255,255,255,0.42)"
                        strokeWidth="1"
                    />
                    <circle
                        cx={activePoint.x}
                        cy={activePoint.y}
                        r="5"
                        fill="#BB86FC"
                        stroke="#FFFFFF"
                        strokeWidth="2"
                    />
                    <rect
                        x={tooltipX}
                        y={tooltipY}
                        width={tooltipWidth}
                        height={tooltipHeight}
                        rx="4"
                        fill="#2F2F2F"
                        stroke="rgba(255,255,255,0.18)"
                        filter="drop-shadow(0 8px 16px rgba(0,0,0,0.35))"
                    />
                    <text
                        x={tooltipX + tooltipPaddingX}
                        y={tooltipY + 22}
                        fill="#FFFFFF"
                        fontSize="13"
                        fontWeight="600"
                    >
                        {activeItem.date}
                    </text>
                    <text x={tooltipX + tooltipPaddingX} y={tooltipY + 48} fill={barColor} fontSize="13">
                        {tooltipVolumeText}
                    </text>
                    <text x={tooltipX + tooltipPaddingX} y={tooltipY + 70} fill="#BB86FC" fontSize="13">
                        {tooltipUsersText}
                    </text>
                </g>
            )}
        </Box>
    );
}

function TrendCard({ item, t, fullWidth = false }: { item: TrendItem; t: TranslateFn; fullWidth?: boolean }) {
    const tradingVolumeLabel = t('module.dashboardV3.overview.trend.tradingVolume', { _: 'Trading Volume' });
    const tradingUsersLabel = t('module.dashboardV3.overview.trend.tradingUsers', { _: 'Trading Users' });

    return (
        <OverviewPanel
            title={item.title}
            right={
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.3, bgcolor: item.barColor }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13 }}>{tradingVolumeLabel}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                        <Box sx={{ width: 18, height: 2, bgcolor: '#BB86FC' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13 }}>{tradingUsersLabel}</Typography>
                    </Stack>
                </Stack>
            }
        >
            <ComboTrendChart
                data={item.data}
                barColor={item.barColor}
                volumeLabel={item.volumeLabel}
                usersLabel={tradingUsersLabel}
                userUnit={t('module.dashboardV3.overview.trend.peopleUnit', { _: 'users' })}
                fullWidth={fullWidth}
            />
        </OverviewPanel>
    );
}

export function OverviewPage() {
    const t = useTranslate();
    const [activeProductTab, setActiveProductTab] = useState<ProductTabValue>('overview');

    const {
        range,
        setRange,
        customFrom,
        setCustomFrom,
        customTo,
        setCustomTo,
        rangeParams,
    } = useDashboardV3Range<OverviewRangeValue>('today');

    const overviewParams = useMemo(
        () => ({
            range: rangeParams.range,
            startDate: rangeParams.startDate,
            endDate: rangeParams.endDate,
        }),
        [rangeParams.range, rangeParams.startDate, rangeParams.endDate]
    );

    const {
        overviewKpi,
        productKpi,
        trends,
        retentionMatrices,
        kpiLoading,
        retentionLoading,
        trendsLoading,
    } = useDashboardV3OverviewData(overviewParams, activeProductTab);

    const isOverviewTab = activeProductTab === 'overview';
    const kpiCards = useMemo(
        () => (isOverviewTab ? buildOverviewKpiCards(overviewKpi, t) : buildProductKpiCards(productKpi, t)),
        [isOverviewTab, overviewKpi, productKpi, t]
    );
    const financeCards = useMemo(() => buildFinanceCards(overviewKpi, t), [overviewKpi, t]);
    const depositToTradeCards = useMemo(
        () => buildDepositToTradeCards(overviewKpi.depositToTradeConversions, t),
        [overviewKpi.depositToTradeConversions, t]
    );
    const retentionByProductCards = useMemo(
        () => buildRetentionByProductCards(overviewKpi.d1RetentionByProduct, t),
        [overviewKpi.d1RetentionByProduct, t]
    );
    const trendCards = useMemo(() => buildTrendItems(trends, t), [trends, t]);

    return (
        <Box sx={{ mt: 3, color: '#FFFFFF' }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.6 }}>
                        {t('module.dashboardV3.overview.title', { _: 'Data Overview' })}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                        {t('module.dashboardV3.overview.subtitle', { _: 'Cross-module core metrics' })}
                    </Typography>
                </Box>

                <DashboardV3RangeSelector
                    value={range}
                    options={OVERVIEW_RANGE_LABELS}
                    customFrom={customFrom}
                    customTo={customTo}
                    onChange={(value) => setRange(value as OverviewRangeValue)}
                    onCustomFromChange={setCustomFrom}
                    onCustomToChange={setCustomTo}
                />
            </Stack>

            <Stack direction="row" spacing={3} sx={{ mb: 2.5, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                {OVERVIEW_PRODUCT_TABS.map((item) => {
                    const active = activeProductTab === item.value;
                    const underlineColor = OVERVIEW_TAB_COLORS[item.value] ?? '#BB86FC';

                    return (
                        <Box
                            key={item.value}
                            onClick={() => setActiveProductTab(item.value as ProductTabValue)}
                            sx={{
                                position: 'relative',
                                pb: 1.4,
                                color: active ? '#FFFFFF' : 'rgba(255,255,255,0.55)',
                                fontSize: 14,
                                fontWeight: active ? 500 : 400,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: -1,
                                    height: 2,
                                    bgcolor: underlineColor,
                                    transform: active ? 'scaleX(1)' : 'scaleX(0)',
                                    transformOrigin: 'left',
                                    transition: 'transform .2s',
                                },
                            }}
                        >
                            {t(item.labelKey, { _: item.label })}
                        </Box>
                    );
                })}
            </Stack>

            <Box sx={{ position: 'relative', minHeight: 360 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    {kpiCards.map((item) => (
                        <OverviewMetricCard key={item.key} item={item} loading={kpiLoading} />
                    ))}
                </Box>

                {/*
                    2026-09-10：上游各产品的充值数据口径互相对不齐（PG和Mongo数据对不齐，以及PG各自的账本分不清内部转账和真实
                    充值，导致「既入金又交易」人数可能超过入金人数），且短期内拿不到能对齐的数据，通知先把这四张卡的展示去掉。
                    {isOverviewTab && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    lg: 'repeat(4, minmax(0, 1fr))',
                                },
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            {depositToTradeCards.map((item) => (
                                <OverviewMetricCard key={item.key} item={item} loading={kpiLoading} />
                            ))}
                        </Box>
                    )}
                */}

                {isOverviewTab && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                                lg: 'repeat(4, minmax(0, 1fr))',
                            },
                            gap: 2,
                            mb: 2.5,
                        }}
                    >
                        {retentionByProductCards.map((item) => (
                            <OverviewMetricCard key={item.key} item={item} loading={kpiLoading} />
                        ))}
                    </Box>
                )}

                {isOverviewTab && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(3, minmax(0, 1fr))',
                            },
                            gap: 2,
                            mb: 2.5,
                        }}
                    >
                        {financeCards.map((item) => (
                            <FinanceMetricCard key={item.key} item={item} loading={kpiLoading} />
                        ))}
                    </Box>
                )}

                {/*
                    2026-09-08：转化漏斗的方案还没最终定（口径可能还要改，也可能下个迭代才做），先把展示隐藏掉
                    {isOverviewTab && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    lg: '1fr 1fr',
                                },
                                gap: 2,
                                mb: 2.5,
                            }}
                        >
                            {funnel.funnels.map((item) => (
                                <ConversionFunnel
                                    key={item.productType}
                                    items={buildFunnelItems(funnel.newUsers, item, t)}
                                    t={t}
                                    productType={item.productType}
                                />
                            ))}
                        </Box>
                    )}
                */}

                {isOverviewTab && (
                    <Box sx={{ position: 'relative', minHeight: retentionMatrices.length ? undefined : 240, mb: 2.5 }}>
                        <SectionLoadingOverlay show={retentionLoading && retentionMatrices.length === 0} />

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    lg: '1fr 1fr',
                                },
                                gap: 2,
                            }}
                        >
                            {retentionMatrices.map((matrix) => (
                                <RetentionMatrix key={matrix.productType} matrix={matrix} t={t} />
                            ))}
                        </Box>
                    </Box>
                )}

                <Box sx={{ position: 'relative', minHeight: trendCards.length ? undefined : 320, pb: 4 }}>
                    <SectionLoadingOverlay show={trendsLoading && trendCards.length === 0} />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                lg: isOverviewTab ? '1fr 1fr' : '1fr',
                            },
                            gap: 2,
                        }}
                    >
                        {trendCards.map((item) => (
                            <TrendCard key={item.key} item={item} t={t} fullWidth={!isOverviewTab} />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
